import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/legal-hr';

const LegalHRLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Legal & HR Document Hub | Explanatum';
    const desc = 'Generate compliant documents, policies, and contracts in minutes—without copying templates or paying expensive legal retainers.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Legal and HR overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-teal-400/40 bg-teal-500/10 px-4 py-1 text-sm font-semibold text-teal-100">
                <span className="inline-flex h-2 w-2 rounded-full bg-teal-300" />
                AI Legal & HR Document Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Launch policies, contracts, and compliance like a full legal desk.
                <span className="mt-2 block text-blue-600">
                  Stop copy-pasting templates—ship tailored docs backed by AI.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Generate privacy policies, T&Cs, employee handbooks, and HR policies aligned to your jurisdiction. Explanatum replaces risky template downloads and costly advisors with automated playbooks and action-ready documents for growing teams.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_legal_hr_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-teal-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-teal-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/legal-hr"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open legal & HR workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Contract Analyzer catching missing clauses and risk flags
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                  Generators for privacy, T&Cs, NDAs, and HR policies with localisation
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Employee handbook creator aligned with employment best practices
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Legal HR dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-teal-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Compliance pulse</span>
                  <span>Live checklist</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Contract analyzer flagged 3 missing IP clauses • AI suggested language applied • Summary exported to PDF.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Privacy policy refreshed for UK/EU data residency • Change log shared with marketing and product teams.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Employee handbook published with onboarding checklist • HR notified to collect acknowledgements.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Legal confidence without legal bills</h2>
            <p className="mt-4 text-base text-gray-500">Deliver tailored documents, track compliance states, and collaborate across teams without chasing law firms or risking copycat content.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Document generators',
              body: 'Produce privacy policies, T&Cs, NDAs, and contractor agreements tuned to your jurisdictions and industry.'
            }, {
              title: 'Contract analyzer',
              body: 'Upload agreements to detect missing protections, conflicting clauses, and negotiation leverage instantly.'
            }, {
              title: 'Employee handbook builder',
              body: 'Draft onboarding, benefits, and conduct policies with region-specific guidance and easy publishing.'
            }, {
              title: 'Policy compliance tracking',
              body: 'Keep a log of updates, acknowledgements, and version history for audits or investor due diligence.'
            }, {
              title: 'AI advisor workflows',
              body: 'Leverage the AI Business Consultant for implementation tips, risk commentary, and stakeholder summaries.'
            }, {
              title: 'Team collaboration',
              body: 'Share drafts with HR, finance, and leadership—collect feedback and approvals in one workspace.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams ditch copycat templates and advisor retainers</h2>
            <p className="mt-4 text-base text-gray-500">Ship compliant documents fast while keeping governance intact—without waiting on agencies or risking copyright issues.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Legal & HR Document Hub',
              points: [
                'End-to-end suite for policies, contracts, and HR playbooks',
                'Jurisdiction-aware templates with AI-powered updates',
                'Contract analyzer with red flag detection and suggestions',
                'Collaboration and version control built in',
                'Predictable subscription pricing for the whole team'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Copying other companies’ documents',
              points: [
                'Risk of outdated clauses and legal exposure',
                'No tailoring to your jurisdiction or business model',
                'Copyright concerns and potential compliance breaches',
                'Manual formatting and version chaos across teams',
                'No support for onboarding or stakeholder alignment'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Hiring legal or HR advisors',
              points: [
                '£250+/hour or multi-thousand monthly retainers',
                'Turnaround times measured in days or weeks',
                'Knowledge locked in emails and static PDFs',
                'Limited availability when policies need quick updates',
                'Additional fees for every new document or review'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-teal-400/30 bg-teal-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need compliant policies live before the next audit?”</h3>
            <p className="mt-3 text-base text-gray-600">Spin up tailored documents, run them through the analyzer, and share sign-off checklists without waiting on external advisors.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_legal_hr_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-teal-500 via-sky-500 to-indigo-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-teal-400/30 hover:scale-[1.02]"
              >
                Launch legal & HR hub now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where fast legal & HR execution wins the day</h2>
            <p className="mt-4 text-base text-gray-500">Equip founders, HR leads, and operations teams with compliant documents at every growth stage.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Startup teams scaling headcount',
              body: 'Roll out employee handbooks, NDAs, and contractor agreements before onboarding new hires.'
            }, {
              title: 'Regulated businesses',
              body: 'Keep privacy policies and compliance documentation current across jurisdictions without delay.'
            }, {
              title: 'Agencies & fractional HR leads',
              body: 'Deliver polished, client-ready documents fast while tracking sign-offs and version history.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default LegalHRLanding;
